import { Component, OnInit, OnDestroy, Input } from '@angular/core';
import { CommonModule } from '@angular/common';
import { ReactiveFormsModule, FormBuilder, FormGroup, FormArray, Validators } from '@angular/forms';
import { NgbActiveModal, NgbTooltipModule } from '@ng-bootstrap/ng-bootstrap';
import { Subscription } from 'rxjs';

import { UserService, LicenseType, RankType } from '../../services/user.service';

@Component({
  selector: 'app-edit-user-modal',
  standalone: true,
  imports: [
    CommonModule,
    ReactiveFormsModule,
    NgbTooltipModule
  ],
  templateUrl: './edit-user-modal.component.html',
  styleUrl: './edit-user-modal.component.scss'
})
export class EditUserModalComponent implements OnInit, OnDestroy {
  @Input() userData: any; // User data passed from parent component
  
  editUserForm!: FormGroup;
  currentStep = 1;
  totalSteps = 3;
  isSubmitting = false;
  licenses: LicenseType[] = [];
  ranks: RankType[] = [];
  isLoadingData = false;
  
  private subscription = new Subscription();

  constructor(
    private fb: FormBuilder,
    public activeModal: NgbActiveModal,
    private userService: UserService
  ) {
    this.initForm();
  }

  ngOnInit(): void {
    this.loadSelectData();
    this.setupAddressWatchers();
    this.initializeDynamicArrays();
    
    // Populate form with user data if provided
    if (this.userData) {
      this.populateForm();
    }
  }

  ngOnDestroy(): void {
    this.subscription.unsubscribe();
  }

  /**
   * Initialize the reactive form
   */
  private initForm(): void {
    this.editUserForm = this.fb.group({
      // Personal Information
      firstName: ['', [Validators.minLength(2)]],
      lastName: ['', [Validators.minLength(2)]],
      email: ['', [Validators.email]],
      cid: ['', [Validators.pattern(/^\d{13}$/)]],
      phoneNumber: ['', [Validators.pattern(/^[0-9]{10}$/)]],
      gender: [''],
      dateOfBirth: [''],
      
      // Registered Address Information
      registeredHouseNumber: [''],
      registeredProjectName: [''],
      registeredSoi: [''],
      registeredRoad: [''],
      registeredSubdistrict: [''],
      registeredDistrict: [''],
      registeredProvince: [''],
      registeredPostalCode: ['', Validators.pattern(/^\d{5}$/)],
      
      // Current Address Information
      currentHouseNumber: [''],
      currentProjectName: [''],
      currentSoi: [''],
      currentRoad: [''],
      currentSubdistrict: [''],
      currentDistrict: [''],
      currentProvince: [''],
      currentPostalCode: ['', Validators.pattern(/^\d{5}$/)],
      
      // Address Options
      sameAsRegistered: [false],
      
      // Professional Information
      organization: [''],
      licenseType: [''],
      rankLevel: [''],
      
      // VAT Member Information
      vatMemberType: [''],
      vatMemberExpired: [''],
      vatMemberId: [''],
      
      // Valuer Rank Information
      valuerRank: [''],
      valuerRankExpired: [''],
      
      // VAT Information
      vatId: [''],
      vatExpired: [''],
      
      // Valuer Information
      valuerId: [''],
      valuerExpired: [''],
      
      // Valuation Experience (Dynamic Array)
      valuationExperience: this.fb.array([]),
      
      // Education Background (Dynamic Array)
      educationBackground: this.fb.array([]),
      
      // Professional Training (Dynamic Array)
      professionalTraining: this.fb.array([]),
      
      // Other Training (Dynamic Array)
      otherTraining: this.fb.array([]),
      
      // Status
      isActive: [true]
    });
  }

  /**
   * Populate form with existing user data
   */
  private populateForm(): void {
    if (!this.userData) return;

    // Populate basic fields
    Object.keys(this.editUserForm.controls).forEach(key => {
      if (this.userData[key] !== undefined && key !== 'valuationExperience' && 
          key !== 'educationBackground' && key !== 'professionalTraining' && 
          key !== 'otherTraining') {
        this.editUserForm.get(key)?.setValue(this.userData[key]);
      }
    });

    // Populate dynamic arrays
    this.populateDynamicArrays();
  }

  /**
   * Populate dynamic arrays with existing data
   */
  private populateDynamicArrays(): void {
    // Clear existing arrays
    this.valuationExperienceArray.clear();
    this.educationBackgroundArray.clear();
    this.professionalTrainingArray.clear();
    this.otherTrainingArray.clear();

    // Populate valuation experience
    if (this.userData.valuationExperience?.length > 0) {
      this.userData.valuationExperience.forEach((exp: any) => {
        const expGroup = this.fb.group({
          organization: [exp.organization || '', Validators.required],
          position: [exp.position || '', Validators.required],
          years: [exp.years || 0, [Validators.required, Validators.min(0)]]
        });
        this.valuationExperienceArray.push(expGroup);
      });
    } else {
      this.addValuationExperience();
    }

    // Populate education background
    if (this.userData.educationBackground?.length > 0) {
      this.userData.educationBackground.forEach((edu: any) => {
        const eduGroup = this.fb.group({
          institution: [edu.institution || '', Validators.required],
          degree: [edu.degree || '', Validators.required],
          yearComplete: [edu.yearComplete || '', [Validators.required, Validators.min(2500), Validators.max(2600)]]
        });
        this.educationBackgroundArray.push(eduGroup);
      });
    } else {
      this.addEducationBackground();
    }

    // Populate professional training
    if (this.userData.professionalTraining?.length > 0) {
      this.userData.professionalTraining.forEach((training: any) => {
        const trainingGroup = this.fb.group({
          trainingTopic: [training.trainingTopic || '', Validators.required],
          organizer: [training.organizer || '', Validators.required],
          cpdHours: [training.cpdHours || 0, [Validators.min(0), Validators.max(100)]],
          trainingDate: [training.trainingDate || '', Validators.required]
        });
        this.professionalTrainingArray.push(trainingGroup);
      });
    } else {
      this.addProfessionalTraining();
    }

    // Populate other training
    if (this.userData.otherTraining?.length > 0) {
      this.userData.otherTraining.forEach((training: any) => {
        const trainingGroup = this.fb.group({
          courseName: [training.courseName || '', Validators.required],
          organizer: [training.organizer || '', Validators.required],
          cpdHours: [training.cpdHours || 0, [Validators.min(0), Validators.max(100)]],
          courseDate: [training.courseDate || '', Validators.required],
          status: [training.status || '']
        });
        this.otherTrainingArray.push(trainingGroup);
      });
    } else {
      this.addOtherTraining();
    }
  }

  /**
   * Initialize dynamic arrays with default rows (only if no existing data)
   */
  private initializeDynamicArrays(): void {
    if (!this.userData) {
      // Add default rows only for new users
      this.addValuationExperience();
      this.addEducationBackground();
      this.addProfessionalTraining();
      this.addOtherTraining();
    }
  }

  /**
   * Setup watchers for registered address fields to auto-copy when checkbox is checked
   */
  private setupAddressWatchers(): void {
    const registeredFields = [
      'registeredHouseNumber',
      'registeredProjectName',
      'registeredSoi',
      'registeredRoad',
      'registeredSubdistrict',
      'registeredDistrict',
      'registeredProvince',
      'registeredPostalCode'
    ];

    registeredFields.forEach(fieldName => {
      const control = this.editUserForm.get(fieldName);
      if (control) {
        const subscription = control.valueChanges.subscribe(() => {
          if (this.editUserForm.get('sameAsRegistered')?.value) {
            this.copyRegisteredToCurrent();
          }
        });
        this.subscription.add(subscription);
      }
    });
  }

  /**
   * Load select data (licenses, ranks)
   */
  private loadSelectData(): void {
    this.isLoadingData = true;
    
    // Load actual data from service
    const licensesSub = this.userService.getAllLicenses().subscribe({
      next: (licenses) => {
        this.licenses = licenses;
        this.isLoadingData = false;
      },
      error: (error) => {
        console.error('Error loading licenses:', error);
        this.licenses = [];
        this.isLoadingData = false;
      }
    });
    
    const ranksSub = this.userService.getAllRanks().subscribe({
      next: (ranks) => {
        this.ranks = ranks;
      },
      error: (error) => {
        console.error('Error loading ranks:', error);
        this.ranks = [];
      }
    });
    
    this.subscription.add(licensesSub);
    this.subscription.add(ranksSub);
  }

  /**
   * Navigation methods
   */
  nextStep(): void {
    if (this.currentStep < this.totalSteps) {
      this.currentStep++;
    }
  }

  prevStep(): void {
    if (this.currentStep > 1) {
      this.currentStep--;
    }
  }

  goToStep(step: number): void {
    if (step >= 1 && step <= this.totalSteps) {
      this.currentStep = step;
    }
  }

  /**
   * Form validation methods
   */
  isStepValid(step: number): boolean {
    switch (step) {
      case 1:
        return !!(this.editUserForm.get('firstName')?.valid && 
                  this.editUserForm.get('lastName')?.valid &&
                  this.editUserForm.get('email')?.valid);
      case 2:
        return true; // Professional info is mostly optional
      case 3:
        return true; // Review step
      default:
        return false;
    }
  }

  isStepCompleted(step: number): boolean {
    return step < this.currentStep;
  }

  isStepCurrent(step: number): boolean {
    return step === this.currentStep;
  }

  getProgressPercentage(): number {
    return (this.currentStep / this.totalSteps) * 100;
  }

  /**
   * Form submission
   */
  onSubmit(): void {
    if (this.editUserForm.valid) {
      this.isSubmitting = true;
      
      const formData = this.editUserForm.value;
      console.log('Updating user:', formData);
      
      // Mock API call - replace with actual service
      setTimeout(() => {
        this.isSubmitting = false;
        this.activeModal.close({
          success: true,
          userData: formData,
          action: 'update'
        });
      }, 2000);
    } else {
      // Mark all fields as touched to show validation errors
      Object.keys(this.editUserForm.controls).forEach(key => {
        this.editUserForm.get(key)?.markAsTouched();
      });
    }
  }

  /**
   * Get step title
   */
  getStepTitle(step: number): string {
    switch (step) {
      case 1: return 'ข้อมูลส่วนตัว';
      case 2: return 'ข้อมูลการทำงาน';
      case 3: return 'ตรวจสอบและยืนยัน';
      default: return '';
    }
  }

  /**
   * Get step icon
   */
  getStepIcon(step: number): string {
    switch (step) {
      case 1: return 'bi-person-circle';
      case 2: return 'bi-briefcase';
      case 3: return 'bi-check-circle';
      default: return 'bi-circle';
    }
  }

  /**
   * Address management methods
   */
  onSameAsRegisteredChange(event: any): void {
    const isChecked = event.target.checked;
    this.editUserForm.get('sameAsRegistered')?.setValue(isChecked);
    
    if (isChecked) {
      this.copyRegisteredToCurrent();
      this.toggleCurrentAddressFields(false);
    } else {
      this.toggleCurrentAddressFields(true);
    }
  }

  private copyRegisteredToCurrent(): void {
    const registeredFields = [
      'registeredHouseNumber',
      'registeredProjectName', 
      'registeredSoi',
      'registeredRoad',
      'registeredSubdistrict',
      'registeredDistrict',
      'registeredProvince',
      'registeredPostalCode'
    ];

    registeredFields.forEach(fieldName => {
      const registeredValue = this.editUserForm.get(fieldName)?.value;
      const currentFieldName = fieldName.replace('registered', 'current');
      this.editUserForm.get(currentFieldName)?.setValue(registeredValue);
    });
  }

  private toggleCurrentAddressFields(enabled: boolean): void {
    const currentFields = [
      'currentHouseNumber',
      'currentProjectName',
      'currentSoi', 
      'currentRoad',
      'currentSubdistrict',
      'currentDistrict',
      'currentProvince',
      'currentPostalCode'
    ];

    currentFields.forEach(fieldName => {
      const control = this.editUserForm.get(fieldName);
      if (enabled) {
        control?.enable();
      } else {
        control?.disable();
      }
    });
  }

  /**
   * Input formatting methods
   */
  onPhoneInput(event: any): void {
    let value = event.target.value.replace(/\D/g, '');
    if (value.length > 10) {
      value = value.substring(0, 10);
    }
    this.editUserForm.get('phoneNumber')?.setValue(value);
  }

  onCidInput(event: any): void {
    let value = event.target.value.replace(/\D/g, '');
    if (value.length > 13) {
      value = value.substring(0, 13);
    }
    this.editUserForm.get('cid')?.setValue(value);
  }

  /**
   * Validation helper methods
   */
  hasError(fieldName: string): boolean {
    const field = this.editUserForm.get(fieldName);
    return field ? field.invalid && (field.dirty || field.touched) : false;
  }

  getErrorMessage(fieldName: string): string {
    const field = this.editUserForm.get(fieldName);
    if (field?.errors) {
      if (field.errors['required']) return 'กรุณากรอกข้อมูลนี้';
      if (field.errors['email']) return 'รูปแบบอีเมลไม่ถูกต้อง';
      if (field.errors['pattern']) return 'รูปแบบข้อมูลไม่ถูกต้อง';
      if (field.errors['minlength']) return `ต้องมีอย่างน้อย ${field.errors['minlength'].requiredLength} ตัวอักษร`;
    }
    return '';
  }

  /**
   * Dynamic array getters
   */
  get valuationExperienceArray(): FormArray {
    return this.editUserForm.get('valuationExperience') as FormArray;
  }

  get educationBackgroundArray(): FormArray {
    return this.editUserForm.get('educationBackground') as FormArray;
  }

  get professionalTrainingArray(): FormArray {
    return this.editUserForm.get('professionalTraining') as FormArray;
  }

  get otherTrainingArray(): FormArray {
    return this.editUserForm.get('otherTraining') as FormArray;
  }

  /**
   * Dynamic array management methods
   */
  addValuationExperience(): void {
    const expGroup = this.fb.group({
      organization: ['', Validators.required],
      position: ['', Validators.required],
      years: [0, [Validators.required, Validators.min(0)]]
    });
    this.valuationExperienceArray.push(expGroup);
  }

  removeValuationExperience(index: number): void {
    if (this.valuationExperienceArray.length > 1) {
      this.valuationExperienceArray.removeAt(index);
    }
  }

  addEducationBackground(): void {
    const eduGroup = this.fb.group({
      institution: ['', Validators.required],
      degree: ['', Validators.required],
      yearComplete: ['', [Validators.required, Validators.min(2500), Validators.max(2600)]]
    });
    this.educationBackgroundArray.push(eduGroup);
  }

  removeEducationBackground(index: number): void {
    if (this.educationBackgroundArray.length > 1) {
      this.educationBackgroundArray.removeAt(index);
    }
  }

  addProfessionalTraining(): void {
    const trainingGroup = this.fb.group({
      trainingTopic: ['', Validators.required],
      organizer: ['', Validators.required],
      cpdHours: [0, [Validators.min(0), Validators.max(100)]],
      trainingDate: ['', Validators.required]
    });
    this.professionalTrainingArray.push(trainingGroup);
  }

  removeProfessionalTraining(index: number): void {
    if (this.professionalTrainingArray.length > 1) {
      this.professionalTrainingArray.removeAt(index);
    }
  }

  onTrainingTopicChange(index: number, event: any): void {
    const selectedTopic = event.target.value;
    const trainingGroup = this.professionalTrainingArray.at(index);
    
    // Auto-set organizer based on training topic
    if (selectedTopic.includes('VAT') || selectedTopic.includes('การประเมินค่าอสังหาริมทรัพย์') || 
        selectedTopic.includes('จรรยาบรรณ') || selectedTopic.includes('มาตรฐาน')) {
      trainingGroup.get('organizer')?.setValue('VAT');
    } else if (selectedTopic.includes('หลักทรัพย์') || selectedTopic.includes('การเงิน') || 
               selectedTopic.includes('กฎหมายตลาดทุน') || selectedTopic.includes('กำกับดูแล')) {
      trainingGroup.get('organizer')?.setValue('กลต.');
    }
  }

  addOtherTraining(): void {
    const trainingGroup = this.fb.group({
      courseName: ['', Validators.required],
      organizer: ['', Validators.required],
      cpdHours: [0, [Validators.min(0), Validators.max(100)]],
      courseDate: ['', Validators.required],
      status: ['']
    });
    this.otherTrainingArray.push(trainingGroup);
  }

  removeOtherTraining(index: number): void {
    if (this.otherTrainingArray.length > 1) {
      this.otherTrainingArray.removeAt(index);
    }
  }

  /**
   * CPD calculation methods
   */
  getTotalCPDHours(): number {
    let totalHours = 0;
    this.professionalTrainingArray.controls.forEach(control => {
      const hours = control.get('cpdHours')?.value || 0;
      totalHours += parseFloat(hours);
    });
    return totalHours;
  }

  getTotalOtherCPDHours(): number {
    let totalHours = 0;
    this.otherTrainingArray.controls.forEach(control => {
      const hours = control.get('cpdHours')?.value || 0;
      totalHours += parseFloat(hours);
    });
    return totalHours;
  }

  /**
   * Display helper methods
   */
  getLicenseName(licenseType: string): string {
    const license = this.licenses.find(l => l.licenseType === licenseType);
    return license ? license.name : licenseType;
  }

  getRankName(rankLevel: string): string {
    const levelNum = parseInt(rankLevel, 10);
    const rank = this.ranks.find(r => r.level === levelNum);
    return rank ? rank.name : rankLevel;
  }

  formatDate(dateString: string): string {
    if (!dateString) return '-';
    const date = new Date(dateString);
    return date.toLocaleDateString('th-TH');
  }

  getFullAddress(type: 'registered' | 'current'): string {
    const prefix = type === 'registered' ? 'registered' : 'current';
    const addressParts: string[] = [];
    
    const houseNumber = this.editUserForm.get(`${prefix}HouseNumber`)?.value;
    const projectName = this.editUserForm.get(`${prefix}ProjectName`)?.value;
    const soi = this.editUserForm.get(`${prefix}Soi`)?.value;
    const road = this.editUserForm.get(`${prefix}Road`)?.value;
    const subdistrict = this.editUserForm.get(`${prefix}Subdistrict`)?.value;
    const district = this.editUserForm.get(`${prefix}District`)?.value;
    const province = this.editUserForm.get(`${prefix}Province`)?.value;
    const postalCode = this.editUserForm.get(`${prefix}PostalCode`)?.value;
    
    if (houseNumber) addressParts.push(`เลขที่ ${houseNumber}`);
    if (projectName) addressParts.push(projectName);
    if (soi) addressParts.push(`ซอย ${soi}`);
    if (road) addressParts.push(`ถนน ${road}`);
    if (subdistrict) addressParts.push(`ตำบล${subdistrict}`);
    if (district) addressParts.push(`อำเภอ${district}`);
    if (province) addressParts.push(`จังหวัด${province}`);
    if (postalCode) addressParts.push(postalCode);
    
    return addressParts.length > 0 ? addressParts.join(' ') : '-';
  }

  /**
   * Modal control methods
   */
  close(): void {
    this.activeModal.close();
  }

  dismiss(): void {
    this.activeModal.dismiss();
  }
}
